<template>
  <div class="cp-edit-menu-item" v-if="item">
    <div class="background" @click="close" />

    <div ref="scrollable" class="wrapper no-scrollbar" :class="{ 'wrapper-tall': tallerImage }" :key="z">
      <div class="close-wrapper" @click="close">
        <button class="delete is-large" />
      </div>
      <figure v-if="pic" class="image">
        <span class="button is-rounded manage-pics-btn" @click="showManagePics = !showManagePics">
          <i class="fa fa-camera-retro" />&nbsp;Manage Pictures
        </span>
        <p v-if="showManagePics" class="is-flex" style="margin-bottom: 4px">
          <span class="button is-fullwidth" @click.stop="triggerPic">Change Picture&nbsp;<i
              class="fa fa-upload fa-sm" /></span>
          <span class="button is-fullwidth" style="margin-left: 8px" @click="removePic">Remove Picture&nbsp;<i
              class="far fa-trash-alt fa-sm" /></span>
        </p>
        <img :class="{ 'tall-img': tallerImage, 'contain-img': containImage }" v-webp="pic" @error="errorImg"
          @click="toggleImageHeight" />
        <i class="fa fa-expand" @click="toggleImageHeight" />
        <i class="fa fa-cog" :class="{ 'has-text-grey': containImage }" @click="toggleImageFit" />
      </figure>
      <figure v-else class="no-pic">
        <span class="button is-rounded upload-btn" @click.stop="triggerPic">
          <i class="fa fa-camera-retro" />&nbsp; Upload Picture </span>&nbsp;
        <span v-if="showColors" class="button is-rounded upload-btn" style="margin-left: 6px; margin-right: 6px"
          @click="item.color = ''"><i class="far fa-trash-alt" /></span>
        <swatches v-model="item.color" style="display: inline-block" colors="material-light" popover-to="left"
          row-length="4" max-height="500" @close="setFillColor">
          <!-- :class="{ 'move-right': showColors }" -->
          <span slot="trigger" class="button is-rounded upload-btn" :style="'background-color:' + item.color"
            @click="showColors = !showColors">
            <i class="fa fa-fill-drip" />
          </span>
        </swatches>
        <span v-if="!showColors" class="button is-rounded upload-btn" style="margin-left: 6px"
          @click="useLogo">Logo</span>
      </figure>
      <div style="margin-bottom: 12px" :class="{ rtl: isRTL }">
        <span v-if="hasMultipleLangs" class="hint lang">{{
          lang.toUpperCase()
        }}</span>
        <i class="hint">{{ $t("menu.item-name") }}</i>
        <input class="input subtitle" style="font-size: 1.2em" v-model="item.name" :placeholder="$t('menu.item-name')" />

        <div v-if="hasMultipleLangs" class="lang-btns">
          <span v-for="l in languages" :key="l" class="button is-small is-uppercase" :class="langClass(l)"
            @click="setContentLang(l)">{{ l }}</span>
        </div>

        <div class="field has-addons" style="margin-top: 10px">
          <p class="control" @click="adjustPrice">
            <a class="button is-static">{{ $t("price") }}</a>
          </p>
          <p class="control has-icons-left">
            <i v-if="item.price_from" class="hint">From {{ currency }}{{ item.price_from }}</i>
            <input class="input" style="width: 140px" type="number" placeholder="Item Price" v-model="item.price" />
            <span class="icon is-small is-left">{{ currency }}</span>
          </p>
        </div>

        <i class="hint">Item Description</i>
        <textarea class="textarea" v-model="item.description" placeholder="✎ Item description" />
      </div>

      <div style="margin-bottom: 8px">
        <i class="hint">Staff Notes</i>
        <textarea class="textarea" v-model="item.notes" :placeholder="$t('✎ Staff notes')" rows="1"
          @focus="scrollToBottom"></textarea>
      </div>

      <div class="has-text-right">
        <!-- <i class="hint">SKU</i> -->
        <input class="input is-small is-pulled-left" v-model="item.sku" placeholder="SKU / ID"
          style="display: inline-block; width: 120px" />
        <span v-if="edited" class="tag" style="border: 1px solid #ccc; margin-right: 6px" @click="undo">
          Undo Changes &nbsp;<i class="fa fa-undo has-text-grey" />
        </span>
        <span class="has-text-grey tag" style="font-size: 0.8em" @click="autosaveInfo">
          Auto-save&nbsp;
          <span class="has-text-success has-text-weight-medium">ON</span></span>
      </div>

      <div class="buttons-wrapper" style="margin-top: 16px">
        <p class="button is-fullwidth is-light" @click="addAnotherItem">
          Save and Add Another Item&nbsp;
          <i class="fa fa-plus-circle has-text-grey" />
        </p>
        <!-- <div class="buttons"> -->
        <div class="is-flex">
          <span class="button is-light is-fullwidth" @click="close">
            Save and Close&nbsp;
            <i class="fa fa-chevron-down has-text-grey" />
          </span>
          <span class="button is-light is-fullwidth" style="margin-left: 8px" @click="nextItem">
            Save and Next&nbsp;
            <i class="fa fa-chevron-right has-text-grey" />
          </span>
        </div>
      </div>

      <div style="height: 60px" />

      <div v-if="item && showDebug" class="item-debug">
        <p class="tag is-dark">{{ lang.toUpperCase() }} - SELECTED</p>
        <p><span class="tag">name</span> {{ item.name }}</p>
        <p><span class="tag">description</span> {{ item.description }}</p>

        <div v-for="l in languages" :key="l + 'l'">
          <p class="tag is-dark">{{ l.toUpperCase() }}</p>
          <p v-for="(v, k) in item[l]" :key="k + v">
            <span class="tag">{{ k }}</span> {{ v }}
          </p>
        </div>
      </div>
    </div>

    <div class="bottom-btns">
      <span class="button is-white has-text-danger" @click="deleteItem">
        <i class="far fa-trash-alt fa-sm" style="margin-right: 6px" />
        {{ $t("delete") }}</span>
      <span class="button is-link is-outlined is-pulled-right" @click="gotoAdvanced">
        {{ $t("ecommerce.advanced") }}
        <i class="fa fa-chevron-right fa-sm" style="margin-left: 6px" />
      </span>
    </div>

    <!-- accept="image/*" -->
    <input ref="file" type="file" accept="image/gif,image/png,image/jpeg,image/jpg,image/bmp" style="display: none"
      @change="uploadMenuPic($event)" :key="zFile" />
  </div>
</template>

<script>
import imageCompression from "browser-image-compression";
import Swatches from "vue-swatches";
import "vue-swatches/dist/vue-swatches.min.css"; // Import the styles too, globally

const shortid = require("shortid");

export default {
  name: "CPEditMenuItem",
  components: { Swatches },
  props: {
    itemOrig: Object,
    langInit: String,
    languages: Array,
    currency: String,
    business: Object, // needed business_id for image upload
  },
  data() {
    return {
      fillColor: null,
      showColors: false,
      showManagePics: false,
      showDebug: false,
      item: null, // copy of item for local editing
      orig: null, // save original item in case of undo
      name: null,
      price: null,
      description: null,
      notes: null,
      tallerImage: false,
      isTallWrapper: false,
      containImage: false,
      lang: null, // "en", // selected lang
      z: 0,
      zFile: 100, // attempt to fix Jig bug of uploading same file to different item
      // timer: 0,
    };
  },
  computed: {
    isRTL() {
      return this.lang === "he";
    },
    hasMultipleLangs() {
      return this.languages.length > 1;
    },
    edited() {
      console.log("z", this.z);
      if (!this.orig || !this.item) return false; // this should throw an error
      return !(
        this.orig.pic === this.item.pic &&
        this.orig.name === this.item.name &&
        this.orig.description == this.item.description &&
        this.orig.price === this.item.price &&
        this.orig.notes === this.item.notes &&
        this.orig.sku === this.item.sku &&
        this.orig.color === this.item.color &&
        this.orig.contain_image === this.item.contain_image
      );
    },
    pic() {
      console.log("z", this.z);
      if (!this.item.pic) return null; //return this.$store.state.menuItemPlaceholderPic;
      return this.item.pic;
    },
    itemName() {
      if (this.item[this.lang]) {
        return this.item[this.lang].name;
      }
      return this.item.name;
    },
    itemDescription() {
      if (this.item[this.lang]) {
        return this.item[this.lang].description;
      }
      return this.item.description;
    },
  },
  mounted() {
    console.log("CPEditMenuItem mounted");
    this.orig = this.deepCopy(this.itemOrig);
    this.item = this.deepCopy(this.itemOrig);
    this.setContentLang(this.langInit); //, true);
    this.name = this.item.name;
    this.description = this.item.description;
    this.price = this.item.price;
    this.notes = this.item.notes;
    this.containImage = this.item.contain_image;
    // this.orig = this.deepCopy(this.item);
  },
  methods: {
    useLogo() {
      var url = this.business.profile_pic_url;
      if (!url) {
        this.errorToast("Missing shop profile pic!");
        return;
      }
      this.item.pic = url;
      this.successToast("Set logo as item picture");
      console.log("item", this.item);
      this.z += 1;
    },
    setFillColor() {
      setTimeout(() => (this.showColors = false), 200);
    },
    adjustPrice() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'>
        <i class="fa fa-money-bill-wave"></i>&nbsp;Adjust Price: <b>${this.item.price}</b></p>
        <p style='margin-bottom:10px'>Enter a number between -50 and 50 to apply as percent increase or decrease
        to the price.</p>
        <p style='margin-bottom:10px'>Price will be rounded up to the next integer.</p>
        <div style='font-size:0.8em; font-weight:500; opacity:0.6'>
        <p style='margin-bottom:10px'><b>10</b> means price will increase by 10%</p>
        <p><b>-5</b> means price will decrease by 5%</p>
        </div>
        </div>`,
        input: "number",
        showCloseButton: true,
        confirmButtonText: this.$t("submit"),
        inputValidator: (val) => {
          if (val < -50 || val > 50) {
            return "Enter a percent between -50% and 50%";
          }
        },
      }).then((val) => {
        if (val.value) this.changePriceByPercent(val.value);
      });
    },
    changePriceByPercent(p) {
      var percent = 1 + p / 100;
      var newPrice = Math.ceil(this.item.price * percent);
      var msg = `Price changed from ${this.item.price} to ${newPrice}`;
      this.successToast(msg, "bottom", 9000);
      this.item.price = newPrice;
    },
    addAnotherItem() {
      if (this.edited) {
        this.setContentLang(this.langInit); // saves content and changes back to original language
        this.$emit("save", this.item);
      }
      this.$emit("addAnother");
    },
    nextItem() {
      if (this.edited) {
        this.setContentLang(this.langInit); // saves content and changes back to original language
        this.$emit("save", this.item);
      }
      this.$emit("nextItem", this.item);
    },
    close() {
      if (this.edited) {
        // this.saveContent();
        this.setContentLang(this.langInit); // saves content and changes back to original language
        this.$emit("save", this.item);
      }
      this.$emit("close");
    },
    undo() {
      this.$emit("close"); // close without save
    },
    autosaveInfo() {
      var msg = "Changes are automatically saved<br>when you close this page";
      this.infoToast(msg, "bottom", 5000);
    },
    langClass(l) {
      if (this.lang === l) return "has-text-weight-medium";
      return "is-light has-text-grey";
    },
    deleteItem() {
      this.$emit("delete", this.item);
      this.close();
    },
    gotoAdvanced() {
      // var msg = `Please save menu first <i class='fa fa-save'></i><br>Then go to advanced menu`;
      // this.infoToast(msg, "top");
      this.close();
      this.$emit("gotoAdvanced", this.item.id); // should somehow pass the item id through
    },
    setContentLang(lang) {
      //, isInit) {
      // if (!isInit)
      this.saveContent(); // sets from name to lang.name
      this.lang = lang; // now change the lang
      this.setContent(); // set new content
      // this.$nextTick(() => this.$emit("lang", lang)); // emit to parent so lang display in sync
    },
    setContent() {
      var o = this.item;
      if (o[this.lang]) {
        o.name = o[this.lang].name;
        o.description = o[this.lang].description;
      } else {
        var hint = ` << Set ${this.lang.toUpperCase()}`;
        if (o.name) o.name += hint;
        if (o.description) o.description += hint;
      }
      this.z += 1;
    },
    saveContent() {
      var o = this.item;
      o[this.lang] = {
        name: this.deepCopy(o.name), // sets from name to lang.name
        description: this.deepCopy(o.description), // description to lang.description
      };
      console.log("saveContent", this.lang, o[this.lang]);
    },
    toggleImageHeight() {
      this.tallerImage = !this.tallerImage;
    },
    toggleImageFit() {
      console.log("toggleImageFit", this.item.contain_image);
      this.containImage = !this.containImage;
      this.item.contain_image = !this.item.contain_image;
      this.z += 1;
    },
    scrollToBottom() {
      //   setTimeout(() => this.$refs.scrollable.scrollTo(0, 999), 200);
    },
    errorImg(e) {
      e.target.src = this.$store.state.menuItemPlaceholderPic;
    },
    // submit() {
    //   //   this.$emit("submit", item);
    //   this.$emit("save"); // do only if item has been edited
    //   this.close();
    // },
    removePic() {
      this.item.pic = null;
      this.successToast("Removed Picture");
      console.log("item", this.item);
      this.z += 1;
    },
    triggerPic() {
      var msg = `Opening pic selector <i class='fa fa-spinner fa-spin'></i>`;
      this.infoToast(msg, "top");
      setTimeout(() => this.$refs.file.click(), 300);
    },
    uploadMenuPic(e) {
      console.log("uploadMenuPic", e, e.target);
      if (!e.target.files) return;
      if (e.target.files.length === 0) return;
      var originalFile = e.target.files[0];

      var loader = this.loadingDots();
      var options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 400,
        useWebWorker: true,
      };
      imageCompression(originalFile, options)
        .then((compressedFile) => {
          var sizeKB = Math.round(compressedFile.size / 1024);
          this.console(`compressedFile size ${sizeKB} KB`); // smaller than maxSizeMB
          this.zFile += 1;

          imageCompression
            .getDataUrlFromFile(compressedFile)
            .then((base64) => {
              var data = {
                name: shortid.generate() + ".png",
                content: base64.replace(/^data:image\/[a-z]+;base64,/, ""),
                businessID: this.business.id,
                imageType: "digital_menu",
              };
              this.$store
                .dispatch("uploadImageAWS", data)
                .then((res) => {
                  console.log("uploadImageAWS", res.data);
                  // var url = JSON.parse(res.data.body).Location;
                  var url = res.data.compressed_image_url;
                  this.item.pic = url;
                  this.successToast("Uploaded Picture");
                  console.log("item", this.item);
                  this.z += 1;
                  // this.save();
                  // this.toggle = true; // open pic toggle
                })
                .catch((err) => this.apiError(err))
                .finally(() => loader.hide());
            })
            .catch((err) => this.console("imageData error", err));
        })
        .catch((err) => this.console("imageCompression error", err));
    },
  },
};
</script>

<style scoped>
.cp-edit-menu-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  /* background-color: rgba(255, 255, 255, 0); */
  background-color: rgba(128, 128, 128, 0.5);
  z-index: 21;
  backdrop-filter: blur(1px);
}

.background {
  position: absolute;
  top: 0;
  width: 100vw;
  height: 100vh;
}

.wrapper {
  position: absolute;
  bottom: 0;
  right: 0;
  max-width: 500px;
  height: 100vh;
  /* height: auto;
  max-height: calc(100vh - 56px); */
  overflow: scroll;
  width: 100vw;
  overflow-x: hidden;
  padding: 12px;
  background-color: rgba(255, 255, 255, 1);
  text-align: left;
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  /* transition: all 0.2s linear; */
  box-shadow: 0 0 3px #444;
}

.wrapper-tall {
  border-radius: 0px;
}

.subtitle {
  font-size: 1.4em;
  font-weight: 500;
  margin-bottom: 0;
}

.close-wrapper {
  position: fixed;
  margin-top: -8px;
  right: 0;
  padding: 8px 12px;
  z-index: 2;
}

.textarea {
  border-radius: 2px;
}

figure {
  /* padding: -12px; */
  /* margin-top: 10px; */
  /* width: 100vw; */
  max-width: 500px;
  margin: auto;
  margin-bottom: 10px;
}

.contain-img {
  object-fit: contain !important;
}

img {
  /* margin-top: -12px; */
  /* margin-left: -12px; */
  /* margin-left: -6px; */
  object-fit: cover;
  /* height: 110px; */
  max-height: 200px;
  /* width: calc(100vw + 24px); */
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.14);
  /* margin: auto; */
  /* transition: max-height 0.2s ease;
  transition: border-radius 0.2s ease; */
  transition: all 0.2s ease;
  /* border-bottom-left-radius: 25px;
  border-bottom-right-radius: 25px; */
  border-radius: 16px;
}

.tall-img {
  max-height: 500px;
  border-radius: 2px;
}

.fa-expand {
  position: absolute;
  right: 12px;
  margin-top: -30px;
  color: white;
  text-shadow: 0 0 1px rgba(0, 0, 0, 0.8);
}

.fa-cog {
  position: absolute;
  padding: 12px;
  margin-top: -42px;
  color: white;
  text-shadow: 0 0 1px rgba(0, 0, 0, 0.8);
}

/* .image .fa-camera-retro  */
.edit-pic-btn {
  position: absolute;
  /* left: 8px; */
  padding: 9px;
  height: 32px;
  width: 32px;
  margin-top: -32px;
  /* color: rgb(82, 74, 74); */
  text-shadow: 0 0 1px rgba(0, 0, 0, 0.5);
}

.no-pic {
  margin-bottom: 12px;
}

.description {
  white-space: pre-wrap;
  font-weight: 450;
  opacity: 0.7;
}

.hint {
  position: absolute;
  right: 18px;
  margin-top: -7px;
  font-size: 0.7em;
  font-weight: 500;
  color: rgba(47, 47, 47, 0.5);
  z-index: 2;
  /* background-color: rgba(255, 255, 255, 0.6); */
  background-color: white;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
}

.hint.lang {
  position: absolute;
  right: initial;
  left: 14px;
  margin-top: 3px;
  padding: 0;
  font-size: 0.6em;
}

.lang-btns {
  float: right;
  margin-top: 12px;
}

.lang-btns .button {
  margin-right: 3px;
  padding: 4px;
}

.item-debug {
  padding: 4px 6px;
  background-color: lightyellow;
  font-size: 0.8em;
  margin-top: 8px;
}

.item-debug .tag {
  margin-bottom: 2px;
  padding: 4px;
}

.buttons-wrapper .button {
  border: 1px solid #ccc;
  margin-bottom: 10px;
}

.is-halfwidth {
  width: calc(50% - 5px);
}

.upload-btn {
  border: 1px dashed grey;
  padding: 12px;
}

.manage-pics-btn {
  border: 1px dashed grey;
  color: grey;
  height: 32px;
  margin-bottom: 4px;
  padding: 12px;
}

.move-right {
  margin-left: 50px;
}

.bottom-btns {
  position: absolute;
  /* bottom: 10px; */
  bottom: 0px;
  left: 0;
  padding: 10px;
  padding-bottom: 20px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.9);
}
</style>